import { Countries } from "@/types/Countries";
import { FinanceProductTypes, ISuperPacmanInstallment } from "@/types/ISuperPacman";
import { getDaysInMonth } from "@/shared-helpers/date-time.helper";

export type TranslateFn = (key: string, named?: Record<string, unknown>) => string;

export type OtherAmountVariation = "A" | "B" | "C";

export type OtherAmountSubtitleKey = "other-amount-subtitle-reduce-interest" | "other-amount-subtitle-advance";

export type TimeChip =
  | { kind: "term"; days: number; label: string; amount: string }
  | { kind: "payAll"; label: string; amount: string };

export const INTEREST_SAVING_COUNTRIES: readonly string[] = ["BR", "PA", "CO", "PE"];

export const BIWEEKLY_COUNTRIES: readonly string[] = ["BR", "PA", "CO"];

export function isCreditLineActive(installment: ISuperPacmanInstallment): boolean {
  return Number(installment.financeProduct.creditLinePayoffAmount) > 0;
}

export function isCumulativeBalanceEnabled(installment: ISuperPacmanInstallment): boolean {
  const { country, subProductVersion } = installment.financeProduct;
  if (country === Countries.BRAZIL && subProductVersion === "BMP") {
    return false;
  }
  return installment.cumulativeBalance?.enabled ?? installment.cumulativeBalance?.cumulativeBalanceEnabled ?? false;
}

export function otherAmountSubtitleKey(installment: ISuperPacmanInstallment): OtherAmountSubtitleKey {
  if (INTEREST_SAVING_COUNTRIES.includes(installment.financeProduct.country)) {
    return "other-amount-subtitle-reduce-interest";
  }
  return isCreditLineActive(installment) ? "other-amount-subtitle-reduce-interest" : "other-amount-subtitle-advance";
}

function isCreditLineOnly(installment: ISuperPacmanInstallment): boolean {
  const hasMultiWeekBeyondFirst = installment.daysToAmount.slice(1).some((row) => row.daysPaid >= 14);
  return isCreditLineActive(installment) && !hasMultiWeekBeyondFirst;
}

function isLoanOnly(installment: ISuperPacmanInstallment): boolean {
  return (
    installment.financeProduct.financeProductType === FinanceProductTypes.FEv1 &&
    !isCreditLineActive(installment)
  );
}

function allowsCustomAmountWithoutCumulativeBalance(
  installment: ISuperPacmanInstallment,
  kasnetQrAvailable: boolean,
): boolean {
  const { country, subProductVersion } = installment.financeProduct;
  return (
    (country === Countries.INDONESIA && subProductVersion === "Rental") ||
    (country === Countries.PERU && kasnetQrAvailable)
  );
}

function isCustomAmountAllowed(installment: ISuperPacmanInstallment, kasnetQrAvailable: boolean): boolean {
  const { country, financeProductType } = installment.financeProduct;
  return (
    allowsCustomAmountWithoutCumulativeBalance(installment, kasnetQrAvailable) ||
    (country !== Countries.INDONESIA && country !== Countries.PERU && !isLoanOnly(installment)) ||
    ([Countries.COLOMBIA.toString(), Countries.MEXICO.toString()].includes(country) &&
      financeProductType === FinanceProductTypes.FEv2)
  );
}

export function resolveVariation(
  installment: ISuperPacmanInstallment,
  kasnetQrAvailable = false,
): OtherAmountVariation {
  if (
    !isCumulativeBalanceEnabled(installment) &&
    !allowsCustomAmountWithoutCumulativeBalance(installment, kasnetQrAvailable)
  ) {
    return "C";
  }
  if (isCreditLineOnly(installment)) {
    return "B";
  }
  if (isCustomAmountAllowed(installment, kasnetQrAvailable)) {
    return "A";
  }
  return "C";
}

function weekLabel(weeks: number, t: TranslateFn): string {
  if (weeks === 1) {
    return t("week");
  }
  const key = `week${weeks}`;
  const translated = t(key);
  return translated !== key ? translated : `${weeks} ${t("weeks")}`;
}

function payAllChip(installment: ISuperPacmanInstallment, t: TranslateFn): TimeChip[] {
  const fullPayoffAmount = installment.financeProduct.fullPayoffAmount;
  if (Number(fullPayoffAmount) > 0) {
    return [{ kind: "payAll", label: t("superpacman-remaining-balance"), amount: fullPayoffAmount }];
  }
  return [];
}

export function buildTimeChips(installment: ISuperPacmanInstallment, t: TranslateFn): TimeChip[] {
  if (resolveVariation(installment) === "B") {
    return payAllChip(installment, t);
  }

  const isBiweekly = BIWEEKLY_COUNTRIES.includes(installment.financeProduct.country);
  let rows = installment.daysToAmount.slice(1).slice().sort((a, b) => a.daysPaid - b.daysPaid);

  const isFEv1Mexico =
    installment.financeProduct.country === Countries.MEXICO &&
    installment.financeProduct.financeProductType === FinanceProductTypes.FEv1;
  if (isFEv1Mexico) {
    const canonicalDays = new Set([7, 14, 21, getDaysInMonth()]);
    rows = rows.filter((row) => canonicalDays.has(row.daysPaid));
  }

  const byLabel = new Map<string, TimeChip & { kind: "term" }>();
  for (const row of rows) {
    const days = row.daysPaid;
    let label: string;
    // TODO: verify with PM whether bi-weekly markets should switch to month labels past 4 weeks
    if (isBiweekly) {
      const weeks = Math.round(days / 7);
      if (weeks % 2 !== 0) {
        continue;
      }
      label = weekLabel(weeks, t);
    } else if (days < 28) {
      const weeks = Math.round(days / 7);
      label = weekLabel(weeks, t);
    } else {
      const months = Math.max(1, Math.floor((days + 3) / 30));
      const extraWeeks = Math.round(Math.max(0, days - months * 30) / 7);
      label =
        extraWeeks > 0
          ? t("month-plus-weeks", { months, weeks: extraWeeks })
          : months === 1
            ? t("month")
            : t("months-n", { months });
    }
    const chip: TimeChip & { kind: "term" } = { kind: "term", days, label, amount: row.amount };
    const existing = byLabel.get(label);
    if (!existing || Number(chip.amount) < Number(existing.amount)) {
      byLabel.set(label, chip);
    }
  }

  const chips: TimeChip[] = [...byLabel.values()];
  const fullPayoffAmount = installment.financeProduct.fullPayoffAmount;
  const lastAmount = chips.length ? chips[chips.length - 1].amount : null;
  if (Number(fullPayoffAmount) > 0 && Number(fullPayoffAmount) !== Number(lastAmount)) {
    chips.push({ kind: "payAll", label: t("superpacman-remaining-balance"), amount: fullPayoffAmount });
  }

  return chips;
}

export function handleMaximumAmounts(superPacmanInstallment: ISuperPacmanInstallment): number {
  let maximumAmount = 0;
  const creditLinePayoffAmount = superPacmanInstallment.financeProduct.creditLinePayoffAmount
    ? Number(superPacmanInstallment.financeProduct.creditLinePayoffAmount)
    : null;
  const foMinPaymentWithCbPlusCLPayoffAmount = superPacmanInstallment.financeProduct
    .foMinPaymentWithCbPlusCLPayoffAmount
    ? Number(superPacmanInstallment.financeProduct.foMinPaymentWithCbPlusCLPayoffAmount)
    : null;
  const fullPayoffAmount = Number(superPacmanInstallment.financeProduct.fullPayoffAmount);

  if (creditLinePayoffAmount) {
    maximumAmount = creditLinePayoffAmount;
    if (foMinPaymentWithCbPlusCLPayoffAmount) {
      maximumAmount = foMinPaymentWithCbPlusCLPayoffAmount;
    }
  }

  if (isCumulativeBalanceEnabled(superPacmanInstallment) && fullPayoffAmount) {
    maximumAmount = fullPayoffAmount;
  }

  if (
    superPacmanInstallment.financeProduct.country === Countries.INDONESIA &&
    superPacmanInstallment.financeProduct.subProductVersion === "Rental" &&
    fullPayoffAmount
  ) {
    maximumAmount = fullPayoffAmount;
  }

  return maximumAmount;
}
